/**
 * Freeze V2 — QC Tab
 *
 * Quality Control form for lot numbers, expiration dates, and freezing
 * comments. Fields use the shared Field/FieldLabel wrapper so text inputs
 * match the calendar (DatePickerInput) styling exactly.
 */
import { useCallback } from 'react';

import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import { type QCFormState, useFreezeForm } from '../../contexts';
import { DATE_PICKER_MATCH, MOBILE_BREAKPOINT } from '../../utils/constants';
import { parseWallClock, toWallClockISO } from '@/utils/wallClock';

export function QCTab() {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const { qcForm, dispatch } = useFreezeForm();

  const setField = useCallback(
    (field: keyof QCFormState, value: string) => {
      dispatch({ type: 'SET_QC', field, value });
    },
    [dispatch],
  );

  const setDateField = useCallback(
    (field: keyof QCFormState, date: Date | null) => {
      dispatch({
        type: 'SET_QC',
        field,
        value: date ? toWallClockISO(date) : '',
      });
    },
    [dispatch],
  );

  const gridClasses = isMobile
    ? 'grid grid-cols-1 gap-2.5'
    : 'grid grid-cols-2 gap-x-4 gap-y-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';

  return (
    <div className="flex flex-col gap-3">
      <div className={gridClasses}>
        <Field className="w-full">
          <FieldLabel htmlFor="egLotNumber">EG Lot Number</FieldLabel>
          <Input
            id="egLotNumber"
            className="h-8 text-sm"
            value={qcForm.egLotNumber}
            onChange={(e) => setField('egLotNumber', e.target.value)}
            placeholder="EG lot #"
          />
        </Field>

        <DatePickerInput
          label="EG Expiration Date"
          initialDate={
            parseWallClock(qcForm.egExpDate) ?? undefined
          }
          className={DATE_PICKER_MATCH}
          onChange={(d) => setDateField('egExpDate', d)}
        />

        <DatePickerInput
          label="Trypsin Exp Date"
          initialDate={
            parseWallClock(qcForm.trypsinExpDate) ?? undefined
          }
          className={DATE_PICKER_MATCH}
          onChange={(d) => setDateField('trypsinExpDate', d)}
        />

        <DatePickerInput
          label="EHM Exp Date"
          initialDate={
            parseWallClock(qcForm.ehmExpDate) ?? undefined
          }
          className={DATE_PICKER_MATCH}
          onChange={(d) => setDateField('ehmExpDate', d)}
        />

        <DatePickerInput
          label="Sucrose Exp Date"
          initialDate={
            parseWallClock(qcForm.sucroseExpDate) ?? undefined
          }
          className={DATE_PICKER_MATCH}
          onChange={(d) => setDateField('sucroseExpDate', d)}
        />

        <DatePickerInput
          label="VS Exp Date"
          initialDate={
            parseWallClock(qcForm.vsExpDate) ?? undefined
          }
          className={DATE_PICKER_MATCH}
          onChange={(d) => setDateField('vsExpDate', d)}
        />

        <DatePickerInput
          label="ES Exp Date"
          initialDate={
            parseWallClock(qcForm.esExpDate) ?? undefined
          }
          className={DATE_PICKER_MATCH}
          onChange={(d) => setDateField('esExpDate', d)}
        />

        <Field className="w-full">
          <FieldLabel htmlFor="freezingComment">Freezing Comment</FieldLabel>
          <Input
            id="freezingComment"
            className="h-8 text-sm"
            value={qcForm.freezingComment}
            onChange={(e) => setField('freezingComment', e.target.value)}
            placeholder="Freezing comment"
          />
        </Field>

        <Field className="w-full">
          <FieldLabel htmlFor="strawLotNumber">Straw Lot #</FieldLabel>
          <Input
            id="strawLotNumber"
            className="h-8 text-sm"
            value={qcForm.strawLotNumber}
            onChange={(e) => setField('strawLotNumber', e.target.value)}
            placeholder="Straw lot #"
          />
        </Field>

        <Field className="w-full">
          <FieldLabel htmlFor="vitStrawLotNumber">Vit Straw Lot #</FieldLabel>
          <Input
            id="vitStrawLotNumber"
            className="h-8 text-sm"
            value={qcForm.vitStrawLotNumber}
            onChange={(e) => setField('vitStrawLotNumber', e.target.value)}
            placeholder="Vit straw lot #"
          />
        </Field>

        <Field className="w-full">
          <FieldLabel htmlFor="sterileStrawLotNumber">
            Sterile Straw Lot #
          </FieldLabel>
          <Input
            id="sterileStrawLotNumber"
            className="h-8 text-sm"
            value={qcForm.sterileStrawLotNumber}
            onChange={(e) => setField('sterileStrawLotNumber', e.target.value)}
            placeholder="Sterile straw lot #"
          />
        </Field>

        <Field className="w-full">
          <FieldLabel htmlFor="twelveWellLot">12-Well Lot #</FieldLabel>
          <Input
            id="twelveWellLot"
            className="h-8 text-sm"
            value={qcForm.twelveWellLot}
            onChange={(e) => setField('twelveWellLot', e.target.value)}
            placeholder="12-well lot #"
          />
        </Field>
      </div>
    </div>
  );
}
